import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const api = (path, opts = {}) => fetch(`${API}/api${path}`, { headers: { "Content-Type": "application/json", ...opts.headers }, ...opts }).then(r => { if (!r.ok) throw r; return r.json(); });
const upload = (path, formData) => fetch(`${API}/api${path}`, { method: "POST", body: formData }).then(r => { if (!r.ok) throw r; return r.json(); });

const SECTORS = [
  { name: "Healthcare & Social Care", slug: "healthcare", desc: "Specialist recruitment for healthcare, nursing, social care, and supported living." },
  { name: "IT & Technology", slug: "it-technology", desc: "Technology recruitment from developers and engineers to CTO and IT leadership." },
  { name: "Accounting & Finance", slug: "accounting-finance", desc: "Qualified accountants, finance managers, controllers, and senior finance leadership." },
  { name: "Education & Childcare", slug: "education-childcare", desc: "Education professionals, nursery management, teaching, and childcare leadership." },
  { name: "Commercial Sales & Business Development", slug: "commercial-sales", desc: "Sales, business development, account management, and commercial leadership." },
];

const IMG = {
  hero: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=1600&q=80",
  heroOverlay: "https://images.unsplash.com/photo-1622675363311-3e1904dc1885?w=900&q=80",
  heroVideo: "https://videos.pexels.com/video-files/3205624/3205624-hd_1920_1080_25fps.mp4",
  team: "https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=1400&q=80",
  meeting: "https://images.pexels.com/photos/7698712/pexels-photo-7698712.jpeg?auto=compress&cs=tinysrgb&w=1200",
  office: "https://images.unsplash.com/photo-1497215842964-222b430dc094?w=1200&q=80",
  healthcare: "https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?w=1200&q=80",
  tech: "https://images.unsplash.com/photo-1606857521015-7f9fcf423740?w=1200&q=80",
  finance: "https://images.unsplash.com/photo-1628348068343-c6a848d2b6dd?w=1200&q=80",
  education: "https://images.pexels.com/photos/8364069/pexels-photo-8364069.jpeg?auto=compress&cs=tinysrgb&w=1200",
  sales: "https://images.pexels.com/photos/5439474/pexels-photo-5439474.jpeg?auto=compress&cs=tinysrgb&w=1200",
  skyline: "https://images.unsplash.com/photo-1768402873162-bc882403ec03?w=1600&q=80",
  londonSkyline: "https://images.pexels.com/photos/29253536/pexels-photo-29253536.jpeg?auto=compress&cs=tinysrgb&w=1600",
  candidate: "https://images.unsplash.com/photo-1590650213165-c1fef80648c4?w=900&q=80",
  employer: "https://images.pexels.com/photos/7648312/pexels-photo-7648312.jpeg?auto=compress&cs=tinysrgb&w=900",
  partnership: "https://images.pexels.com/photos/7643738/pexels-photo-7643738.jpeg?auto=compress&cs=tinysrgb&w=1200",
  about: "https://images.unsplash.com/photo-1554902843-260acd0993f8?w=1400&q=80",
};

const SECTOR_IMG = {
  healthcare: IMG.healthcare,
  "it-technology": IMG.tech,
  "accounting-finance": IMG.finance,
  "education-childcare": IMG.education,
  "commercial-sales": IMG.sales,
};

/* ═══ SCROLL FADE OBSERVER ═══ */
function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.style.opacity = 1; el.style.transform = "translateY(0)"; } }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}
function FadeIn({ children, className = "", delay = 0 }) {
  const ref = useFadeIn();
  return <div ref={ref} className={className} style={{ opacity: 0, transform: "translateY(28px)", transition: `opacity 0.8s cubic-bezier(.23,1,.32,1) ${delay}s, transform 0.8s cubic-bezier(.23,1,.32,1) ${delay}s` }}>{children}</div>;
}

/* ═══ HEADER — Solid, anchored, premium ═══ */
function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => { const h = () => setScrolled(window.scrollY > 10); window.addEventListener("scroll", h); return () => window.removeEventListener("scroll", h); }, []);
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${scrolled ? "shadow-[0_2px_24px_rgba(0,0,0,0.06)]" : "shadow-[0_1px_0_rgba(0,0,0,0.04)]"}`} data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-[90px] md:h-[110px]">
        <Link to="/" className="flex items-center flex-shrink-0">
          <img src="/bm-logo.png" alt="Banks Morgan Recruitment" className="h-[80px] md:h-[96px] w-auto" data-testid="site-logo" />
        </Link>
        <nav className="hidden lg:flex items-center gap-9">
          {[["Jobs", "/jobs"], ["Sectors", "/sectors"], ["Candidates", "/candidates"], ["Employers", "/employers"], ["Partnerships", "/partnerships"], ["About", "/about"], ["Contact", "/contact"]].map(([l, p]) => (
            <Link key={p} to={p} className="text-[13px] text-[#4B5563] hover:text-[#0F172A] transition-colors font-medium tracking-wide">{l}</Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/employers" className="px-7 py-3 text-[11px] font-bold uppercase tracking-[0.15em] bg-[#0F172A] text-white rounded-lg hover:bg-[#1E293B] transition-all" data-testid="cta-hire">Hire Talent</Link>
          <Link to="/jobs" className="px-7 py-3 text-[11px] font-bold uppercase tracking-[0.15em] text-[#0F172A] border border-[#CBD5E1] rounded-lg hover:border-[#0F172A] transition-all" data-testid="cta-find-job">Find a Job</Link>
        </div>
        <button onClick={() => setOpen(!open)} className="lg:hidden text-[#0F172A] p-2" data-testid="mobile-menu-toggle">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d={open ? "M6 6l12 12M6 18L18 6" : "M4 7h16M4 12h16M4 17h16"} /></svg>
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-6 py-6 space-y-4 shadow-xl">
          {[["Jobs", "/jobs"], ["Sectors", "/sectors"], ["Candidates", "/candidates"], ["Employers", "/employers"], ["Partnerships", "/partnerships"], ["About", "/about"], ["Contact", "/contact"]].map(([l, p]) => (
            <Link key={p} to={p} onClick={() => setOpen(false)} className="block text-gray-600 hover:text-[#0F172A] text-base font-medium">{l}</Link>
          ))}
          <div className="pt-4 border-t border-gray-100 flex gap-3">
            <Link to="/employers" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-3 text-xs font-bold uppercase tracking-wider bg-[#0F172A] text-white rounded-lg">Hire Talent</Link>
            <Link to="/jobs" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#0F172A] border border-gray-300 rounded-lg">Find a Job</Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* ═══ FOOTER ═══ */
function SiteFooter() {
  return (
    <footer className="bg-[#0F172A] text-gray-400" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
          <div>
            <div className="text-[11px] font-bold text-white uppercase tracking-wider mb-5">For Candidates</div>
            <div className="space-y-3 text-sm"><Link to="/jobs" className="block hover:text-white transition-colors">Search Jobs</Link><Link to="/candidates" className="block hover:text-white transition-colors">Register</Link><Link to="/sectors" className="block hover:text-white transition-colors">Our Sectors</Link></div>
          </div>
          <div>
            <div className="text-[11px] font-bold text-white uppercase tracking-wider mb-5">For Employers</div>
            <div className="space-y-3 text-sm"><Link to="/employers" className="block hover:text-white transition-colors">Hire Talent</Link><Link to="/partnerships" className="block hover:text-white transition-colors">Partnerships</Link><Link to="/contact" className="block hover:text-white transition-colors">Contact</Link></div>
          </div>
          <div>
            <div className="text-[11px] font-bold text-white uppercase tracking-wider mb-5">Company</div>
            <div className="space-y-3 text-sm"><Link to="/about" className="block hover:text-white transition-colors">About</Link><Link to="/contact" className="block hover:text-white transition-colors">Contact</Link></div>
          </div>
          <div>
            <div className="text-[11px] font-bold text-white uppercase tracking-wider mb-5">Get in Touch</div>
            <a href="tel:02045850050" className="block text-xl font-bold text-[#D4AF37] hover:text-[#E5C158] transition-colors mb-2">020 4585 0050</a>
            <p className="text-sm">Banks Morgan Recruitment</p>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-gray-500">&copy; {new Date().getFullYear()} Banks Morgan Recruitment. All rights reserved.</div>
          <div className="flex gap-6 text-xs text-gray-500">
            <Link to="/privacy" className="hover:text-gray-300">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-300">Terms</Link>
            <Link to="/cookies" className="hover:text-gray-300">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ═══ FORM COMPONENTS ═══ */
function F({ label, name, value, onChange, type = "text", required, ...props }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.1em] block mb-2">{label}{required && <span className="text-[#D4AF37] ml-0.5">*</span>}</label>
      {type === "textarea" ? (
        <textarea name={name} value={value} onChange={onChange} required={required} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3.5 text-sm text-[#0F172A] placeholder-gray-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/10 outline-none transition-all min-h-[120px] resize-none" {...props} />
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} required={required} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3.5 text-sm text-[#0F172A] placeholder-gray-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/10 outline-none transition-all" {...props} />
      )}
    </div>
  );
}

function GoldBtn({ children, onClick, disabled, className = "", ...props }) {
  return <button onClick={onClick} disabled={disabled} className={`px-7 py-3.5 text-[12px] font-bold uppercase tracking-[0.15em] bg-[#0F172A] text-white rounded-lg hover:bg-[#1E293B] transition-all disabled:opacity-50 ${className}`} {...props}>{children}</button>;
}

function SuccessCard({ title, msg }) {
  return (
    <div className="bg-white border border-[#D4AF37]/20 rounded-2xl p-16 text-center shadow-sm">
      <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-6"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg></div>
      <h2 className="text-2xl font-bold text-[#0F172A] mb-3">{title}</h2>
      <p className="text-sm text-gray-500 max-w-sm mx-auto">{msg}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/* HOMEPAGE                                    */
/* ═══════════════════════════════════════════ */
export function HomePage() {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  return (
    <div data-testid="public-homepage">
      {/* HERO — Full-width cinematic video */}
      <section className="relative bg-[#0F172A] overflow-hidden" style={{ minHeight: "85vh" }}>
        {/* Video background */}
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoLoaded ? "opacity-0" : "opacity-100"}`} />
          <video
            ref={videoRef}
            autoPlay muted loop playsInline
            onCanPlay={() => setVideoLoaded(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoLoaded ? "opacity-100" : "opacity-0"}`}
            poster={IMG.hero}
          >
            <source src={IMG.heroVideo} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/85 via-[#0F172A]/50 to-[#0F172A]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 to-transparent" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center" style={{ minHeight: "85vh" }}>
          <div className="max-w-2xl py-20">
            <FadeIn>
              <div className="inline-block px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.35em] text-[#D4AF37] border border-[#D4AF37]/40 rounded-full mb-8 backdrop-blur-sm bg-white/5">Specialist Recruitment</div>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.8rem] font-extrabold text-white tracking-tight leading-[1.06] mb-6">
                Connecting exceptional talent with ambitious businesses.
              </h1>
              <p className="text-base md:text-lg text-white/60 leading-relaxed max-w-lg mb-10">
                Specialist sector recruitment, powered by intelligent technology and deep market understanding.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/jobs" className="px-8 py-4 text-[12px] font-bold uppercase tracking-[0.15em] bg-white text-[#0F172A] rounded-lg hover:bg-gray-100 transition-all text-center" data-testid="hero-find-role">Find Your Next Role</Link>
                <Link to="/employers" className="px-8 py-4 text-[12px] font-bold uppercase tracking-[0.15em] text-white border-2 border-white/30 rounded-lg hover:border-white/60 hover:bg-white/5 transition-all text-center" data-testid="hero-hire">Hire Talent</Link>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* GOLD STRIP */}
      <section className="bg-[#D4AF37]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-5 flex flex-wrap items-center justify-center gap-x-12 gap-y-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F172A]/80">
          {SECTORS.map(s => <span key={s.slug}>{s.name}</span>)}
        </div>
      </section>

      {/* SECTORS — varied editorial layout */}
      <section className="bg-white py-28">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <FadeIn>
            <div className="max-w-xl mb-20">
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-3">Our Sectors</div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A] leading-tight">Deep expertise where it matters most.</h2>
            </div>
          </FadeIn>
          {/* Large feature + two small */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <FadeIn>
              <Link to="/sectors/healthcare" className="group block relative h-[420px] rounded-2xl overflow-hidden" data-testid="sector-healthcare">
                <img src={IMG.healthcare} alt="Healthcare & Social Care" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="h-0.5 w-12 bg-[#D4AF37] mb-4" />
                  <h3 className="text-xl font-bold text-white mb-1">Healthcare & Social Care</h3>
                  <p className="text-sm text-white/70">{SECTORS[0].desc}</p>
                </div>
              </Link>
            </FadeIn>
            <FadeIn delay={0.1}>
              <Link to="/sectors/it-technology" className="group block relative h-[420px] rounded-2xl overflow-hidden" data-testid="sector-it-technology">
                <img src={IMG.tech} alt="IT & Technology" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="h-0.5 w-12 bg-[#D4AF37] mb-4" />
                  <h3 className="text-xl font-bold text-white mb-1">IT & Technology</h3>
                  <p className="text-sm text-white/70">{SECTORS[1].desc}</p>
                </div>
              </Link>
            </FadeIn>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[SECTORS[2], SECTORS[3], SECTORS[4]].map((s, i) => (
              <FadeIn key={s.slug} delay={0.15 + i * 0.06}>
                <Link to={`/sectors/${s.slug}`} className="group block relative h-[300px] rounded-2xl overflow-hidden" data-testid={`sector-${s.slug}`}>
                  <img src={SECTOR_IMG[s.slug]} alt={s.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="h-0.5 w-10 bg-[#D4AF37] mb-3" />
                    <h3 className="text-lg font-bold text-white mb-1">{s.name}</h3>
                    <p className="text-xs text-white/60">{s.desc}</p>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* WHY BANKS MORGAN — editorial asymmetric */}
      <section className="bg-[#F8F9FA] py-28 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <FadeIn className="lg:col-span-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Why Banks Morgan</div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A] leading-tight mb-10">The recruitment partner your business deserves.</h2>
              <div className="space-y-8">
                {[
                  { t: "Specialist Sector Knowledge", d: "Our recruiters specialise in specific sectors, building deep networks and understanding the talent landscape that generalist agencies simply cannot." },
                  { t: "Technology-Enabled Search", d: "We combine experienced professionals with intelligent sourcing technology to identify the strongest candidates faster." },
                  { t: "Commercial Partnership", d: "Our recruitment models are designed around your commercial reality — not a one-size-fits-all agency fee." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-5">
                    <div className="w-px bg-[#D4AF37] flex-shrink-0 min-h-[60px]" />
                    <div>
                      <h3 className="text-[15px] font-bold text-[#0F172A] mb-1.5">{item.t}</h3>
                      <p className="text-sm text-gray-400 leading-relaxed">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </FadeIn>
            <FadeIn className="lg:col-span-7 relative" delay={0.15}>
              <div className="relative lg:pl-8">
                <img src={IMG.team} alt="Banks Morgan professionals" className="w-full h-[400px] lg:h-[560px] object-cover rounded-3xl" loading="lazy" />
                <div className="absolute -bottom-8 left-4 lg:left-0 bg-white rounded-xl shadow-xl px-8 py-6 max-w-xs">
                  <div className="text-[10px] text-[#D4AF37] font-bold uppercase tracking-[0.2em] mb-1">Technology-Enabled</div>
                  <div className="text-sm text-[#0F172A] font-medium">Intelligent recruitment powered by modern technology and human expertise.</div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* FULL-WIDTH SKYLINE BAND */}
      <section className="relative h-[320px] md:h-[440px]">
        <img src={IMG.skyline} alt="London business district" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/80 via-[#0F172A]/40 to-transparent flex items-center">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 w-full">
            <FadeIn>
              <div className="max-w-md text-white">
                <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-3">Partnerships</div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4">Recruit regularly? Let's build a partnership.</h2>
                <Link to="/partnerships" className="inline-block px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.15em] bg-[#D4AF37] text-[#0F172A] rounded-lg hover:bg-[#E5C158] transition-all" data-testid="cta-partnership">Discuss a Partnership</Link>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <FadeIn>
            <h2 className="text-3xl font-extrabold text-[#0F172A]">Ready to talk?</h2>
            <p className="text-sm text-gray-400 mt-2">Whether you're hiring or looking for your next role, we're here to help.</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="flex items-center gap-5">
              <a href="tel:02045850050" className="text-2xl font-extrabold text-[#D4AF37] hover:text-[#B8941F] transition-colors">020 4585 0050</a>
              <Link to="/contact" className="px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.15em] text-[#0F172A] border-2 border-[#0F172A] rounded-lg hover:bg-[#0F172A] hover:text-white transition-all">Contact Us</Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/* JOBS + JOB ALERTS                           */
/* ═══════════════════════════════════════════ */
export function JobsPage() {
  const [vacancies, setVacancies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ keyword: "", sector: "", location: "" });
  const [page, setPage] = useState(1);
  const [alertForm, setAlertForm] = useState({ email: "", keyword: "", sector: "", location: "" });
  const [alertSubmitted, setAlertSubmitted] = useState(false);
  const [alertSubmitting, setAlertSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ page: String(page), limit: "15" });
    if (filters.keyword) p.set("keyword", filters.keyword);
    if (filters.sector) p.set("sector", filters.sector);
    if (filters.location) p.set("location", filters.location);
    try { const data = await api(`/public/vacancies?${p}`); setVacancies(data.vacancies || []); setTotal(data.total || 0); } catch {}
    setLoading(false);
  }, [page, filters]);
  useEffect(() => { load(); }, [load]);

  const submitAlert = async () => {
    if (!alertForm.email) { toast.error("Email is required for job alerts"); return; }
    setAlertSubmitting(true);
    try {
      await api("/public/job-alerts", { method: "POST", body: JSON.stringify(alertForm) });
      setAlertSubmitted(true);
      toast.success("Job alert created!");
    } catch { toast.error("Failed to create job alert"); }
    setAlertSubmitting(false);
  };

  return (
    <div className="bg-white min-h-screen pt-8 pb-20" data-testid="jobs-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <FadeIn><div className="max-w-lg mb-12"><div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-3">Opportunities</div><h1 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A]">Find your next role.</h1></div></FadeIn>

        {/* Search filters */}
        <div className="bg-[#F8F9FA] rounded-2xl p-6 md:p-8 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input placeholder="Job title or keyword..." value={filters.keyword} onChange={e => setFilters(f => ({ ...f, keyword: e.target.value }))} onKeyDown={e => e.key === "Enter" && load()} className="bg-white border border-gray-200 rounded-lg px-4 py-3.5 text-sm text-[#0F172A] placeholder-gray-300 focus:border-[#D4AF37] outline-none" data-testid="job-keyword" />
            <select value={filters.sector} onChange={e => { setFilters(f => ({ ...f, sector: e.target.value })); setPage(1); }} className="bg-white border border-gray-200 rounded-lg px-4 py-3.5 text-sm text-[#0F172A] focus:border-[#D4AF37] outline-none appearance-none" data-testid="job-sector-filter">
              <option value="">All Sectors</option>{SECTORS.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
            <input placeholder="Location..." value={filters.location} onChange={e => setFilters(f => ({ ...f, location: e.target.value }))} onKeyDown={e => e.key === "Enter" && load()} className="bg-white border border-gray-200 rounded-lg px-4 py-3.5 text-sm text-[#0F172A] placeholder-gray-300 focus:border-[#D4AF37] outline-none" data-testid="job-location" />
            <GoldBtn onClick={() => { setPage(1); load(); }} data-testid="job-search-btn">Search</GoldBtn>
          </div>
        </div>

        <div className="text-xs text-gray-300 mb-6 font-medium uppercase tracking-wider">{total} {total === 1 ? "vacancy" : "vacancies"}</div>
        {loading ? <div className="text-center py-24 text-gray-300">Loading...</div> : vacancies.length === 0 ? (
          <div className="text-center py-24"><p className="text-gray-400 text-lg">No vacancies match your search.</p><p className="text-sm text-gray-300 mt-2">Try broadening your criteria or check back soon.</p></div>
        ) : (
          <div className="space-y-4">{vacancies.map(v => (
            <FadeIn key={v.id}><Link to={`/jobs/${v.id}`} className="block bg-white border border-gray-100 rounded-xl p-7 hover:shadow-lg hover:border-[#D4AF37]/20 transition-all group" data-testid={`job-card-${v.id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-[#0F172A] group-hover:text-[#D4AF37] transition-colors">{v.title}</h3>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
                    {v.location && <span>{v.location}</span>}{v.hours && <span>{v.hours}</span>}{v.salary && <span className="font-medium text-[#0F172A]">&pound;{v.salary}</span>}{v.sector && <span className="text-[#D4AF37]">{v.sector}</span>}
                  </div>
                </div>
                <span className="text-xs text-gray-300 uppercase tracking-wider font-bold group-hover:text-[#D4AF37] transition-colors whitespace-nowrap">View &rarr;</span>
              </div>
            </Link></FadeIn>
          ))}</div>
        )}

        {/* JOB ALERTS */}
        <FadeIn>
          <div className="mt-16 bg-[#0F172A] rounded-2xl p-8 md:p-12" data-testid="job-alert-section">
            {alertSubmitted ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-5"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg></div>
                <h3 className="text-xl font-bold text-white mb-2">Job Alert Created</h3>
                <p className="text-sm text-white/50">We'll email you when new roles matching your criteria are published.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-2">Never miss an opportunity</div>
                    <h3 className="text-xl md:text-2xl font-extrabold text-white">Create a Job Alert</h3>
                    <p className="text-sm text-white/40 mt-1">Get notified when new roles matching your criteria are published.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <input placeholder="Your email address *" type="email" value={alertForm.email} onChange={e => setAlertForm(f => ({ ...f, email: e.target.value }))} className="bg-white/10 border border-white/10 rounded-lg px-4 py-3.5 text-sm text-white placeholder-white/30 focus:border-[#D4AF37] outline-none" data-testid="alert-email" />
                  <input placeholder="Job title / keyword" value={alertForm.keyword} onChange={e => setAlertForm(f => ({ ...f, keyword: e.target.value }))} className="bg-white/10 border border-white/10 rounded-lg px-4 py-3.5 text-sm text-white placeholder-white/30 focus:border-[#D4AF37] outline-none" data-testid="alert-keyword" />
                  <select value={alertForm.sector} onChange={e => setAlertForm(f => ({ ...f, sector: e.target.value }))} className="bg-white/10 border border-white/10 rounded-lg px-4 py-3.5 text-sm text-white focus:border-[#D4AF37] outline-none appearance-none" data-testid="alert-sector">
                    <option value="" className="text-[#0F172A]">Any Sector</option>{SECTORS.map(s => <option key={s.name} value={s.name} className="text-[#0F172A]">{s.name}</option>)}
                  </select>
                  <input placeholder="Town / City" value={alertForm.location} onChange={e => setAlertForm(f => ({ ...f, location: e.target.value }))} className="bg-white/10 border border-white/10 rounded-lg px-4 py-3.5 text-sm text-white placeholder-white/30 focus:border-[#D4AF37] outline-none" data-testid="alert-location" />
                  <button onClick={submitAlert} disabled={alertSubmitting} className="px-6 py-3.5 text-[12px] font-bold uppercase tracking-[0.15em] bg-[#D4AF37] text-[#0F172A] rounded-lg hover:bg-[#E5C158] transition-all disabled:opacity-50" data-testid="alert-submit">{alertSubmitting ? "Creating..." : "Create Alert"}</button>
                </div>
                <p className="text-[10px] text-white/25 mt-4">By creating a job alert, you consent to receiving email notifications from Banks Morgan Recruitment. You can unsubscribe at any time.</p>
              </>
            )}
          </div>
        </FadeIn>
      </div>
    </div>
  );
}

/* ═══ JOB DETAIL ═══ */
export function JobDetailPage() {
  const { id } = useParams();
  const [vac, setVac] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", current_title: "", cover_letter: "" });
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { api(`/public/vacancies/${id}`).then(setVac).catch(() => {}); }, [id]);

  const apply = async () => {
    setSubmitting(true);
    try {
      const res = await api("/public/apply", { method: "POST", body: JSON.stringify({ ...form, vacancy_id: id }) });
      if (cvFile && res.candidate_id) {
        const fd = new FormData(); fd.append("file", cvFile); fd.append("candidate_id", res.candidate_id);
        await upload("/public/upload-cv", fd);
      }
      toast.success("Application submitted!"); setShowApply(false);
    } catch { toast.error("Failed to submit."); }
    setSubmitting(false);
  };

  if (!vac) return <div className="bg-white min-h-screen pt-32 text-center text-gray-300">Loading...</div>;
  return (
    <div className="bg-[#F8F9FA] min-h-screen pt-8 pb-20" data-testid="job-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/jobs" className="text-[11px] text-[#D4AF37] hover:text-[#B8941F] mb-8 inline-block font-bold uppercase tracking-wider">&larr; Back to Jobs</Link>
        <div className="bg-white rounded-2xl p-8 md:p-14 shadow-sm border border-gray-100">
          <div className="h-1 w-16 bg-[#D4AF37] mb-8 rounded-full" />
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0F172A] mb-4">{vac.title}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-10">{vac.location && <span>{vac.location}</span>}{vac.salary && <span className="font-bold text-[#0F172A]">&pound;{vac.salary}</span>}{vac.sector && <span className="text-[#D4AF37]">{vac.sector}</span>}{vac.employment_type && <span className="capitalize">{vac.employment_type}</span>}</div>
          {(vac.hours || vac.working_pattern || vac.start_date || vac.experience_required) && <div className="grid grid-cols-2 gap-3 text-sm mb-8" data-testid="public-vacancy-facts">{[["Hours", vac.hours], ["Working pattern", vac.working_pattern], ["Start date", vac.start_date], ["Experience", vac.experience_required]].filter(([, x]) => x).map(([k, x]) => <div key={k}><div className="text-[10px] uppercase tracking-wider text-gray-400">{k}</div><div className="font-medium text-[#0F172A]">{x}</div></div>)}</div>}
          {vac.description && <div className="text-sm text-gray-500 leading-[1.8] whitespace-pre-line mb-10">{vac.description}</div>}
          {vac.requirements && <div className="mb-10"><h3 className="text-[11px] font-bold text-[#0F172A] uppercase tracking-[0.15em] mb-3">Requirements</h3><p className="text-sm text-gray-500 whitespace-pre-line leading-[1.8]">{vac.requirements}</p></div>}
          <button onClick={() => setShowApply(true)} className="px-8 py-4 text-[12px] font-bold uppercase tracking-[0.15em] bg-[#0F172A] text-white rounded-lg hover:bg-[#1E293B] transition-all" data-testid="apply-btn">Apply Now</button>
        </div>
        {showApply && (
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) setShowApply(false); }}>
            <div className="bg-white rounded-2xl p-8 md:p-10 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="apply-modal">
              <h2 className="text-xl font-bold text-[#0F172A] mb-6">Apply for {vac.title}</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4"><F label="First Name" name="first_name" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} required /><F label="Last Name" name="last_name" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} required /></div>
                <F label="Email" name="email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
                <F label="Phone" name="phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                <div><label className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.1em] block mb-2">Upload CV<span className="text-gray-300 font-normal ml-1">(PDF, DOC, DOCX — max 10MB)</span></label>
                  <input type="file" accept=".pdf,.doc,.docx,.txt,.rtf" onChange={e => setCvFile(e.target.files?.[0] || null)} className="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-[11px] file:font-bold file:uppercase file:tracking-wider file:bg-gray-100 file:text-[#0F172A] hover:file:bg-gray-200 file:cursor-pointer" data-testid="cv-upload" />
                </div>
                <F label="Cover Letter" name="cover_letter" type="textarea" value={form.cover_letter} onChange={e => setForm(f => ({ ...f, cover_letter: e.target.value }))} placeholder="Tell us why you're a great fit..." />
                <GoldBtn onClick={apply} disabled={submitting} className="w-full" data-testid="submit-application">{submitting ? "Submitting..." : "Submit Application"}</GoldBtn>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══ SECTORS ═══ */
export function SectorsPage() {
  return (
    <div className="bg-white min-h-screen pt-8 pb-20" data-testid="sectors-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <FadeIn><div className="max-w-xl mb-16"><div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-3">Specialist Sectors</div><h1 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A]">Recruitment expertise across five core sectors.</h1></div></FadeIn>
        <div className="space-y-0">
          {SECTORS.map((s, i) => (
            <FadeIn key={s.slug} delay={i * 0.05}>
              <Link to={`/sectors/${s.slug}`} className={`group grid grid-cols-1 md:grid-cols-12 gap-0 items-stretch ${i > 0 ? "border-t border-gray-100" : ""}`} data-testid={`sector-page-${s.slug}`}>
                <div className={`md:col-span-5 flex flex-col justify-center py-12 md:py-20 md:pr-12 ${i % 2 === 1 ? "md:order-2 md:pl-12 md:pr-0" : ""}`}>
                  <div className="h-0.5 w-12 bg-[#D4AF37] mb-6 group-hover:w-20 transition-all duration-500" />
                  <h2 className="text-2xl font-extrabold text-[#0F172A] mb-3 group-hover:text-[#D4AF37] transition-colors">{s.name}</h2>
                  <p className="text-sm text-gray-400 leading-relaxed mb-4">{s.desc}</p>
                  <span className="text-[11px] font-bold text-[#0F172A] uppercase tracking-wider group-hover:text-[#D4AF37] transition-colors">Explore Sector &rarr;</span>
                </div>
                <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <div className="h-[280px] md:h-full min-h-[300px] overflow-hidden">
                    <img src={SECTOR_IMG[s.slug]} alt={s.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
                  </div>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ═══ SECTOR DETAIL PAGES ═══ */
const SECTOR_DETAIL = {
  healthcare: {
    title: "Healthcare & Social Care",
    subtitle: "Specialist recruitment across healthcare, nursing, social care, and supported living.",
    intro: "Banks Morgan partners with NHS trusts, private healthcare providers, care home groups, and social care organisations to source exceptional professionals at every level. From frontline clinical staff to senior leadership, we understand the regulatory landscape, the urgency of healthcare hiring, and the importance of cultural fit in care-driven environments.",
    roles: ["Registered Nurses (RGN, RMN, RNLD)", "Healthcare Assistants & Support Workers", "Nursing Home Managers & Deputy Managers", "Clinical Lead Nurses", "Social Workers & Care Coordinators", "Occupational Therapists & Physiotherapists", "Healthcare Operations & Regional Managers", "Compliance & Quality Assurance Officers", "Supported Living & Domiciliary Care Managers", "Director-level Healthcare Leadership"],
    why: "Healthcare recruitment demands more than matching a CV to a job description. We work closely with hiring managers to understand the clinical environment, team dynamics, and compliance requirements before presenting candidates. Our sector specialists maintain active networks of qualified professionals, many of whom are not actively searching but are open to the right opportunity.",
  },
  "it-technology": {
    title: "IT & Technology",
    subtitle: "From developers and engineers to CTO and IT leadership across all technology disciplines.",
    intro: "The technology sector moves faster than any other. Banks Morgan helps technology companies and IT departments within larger organisations recruit the software engineers, architects, data specialists, and technology leaders they need to grow and innovate. We understand the difference between a full-stack developer and a platform engineer, and we know why it matters.",
    roles: ["Software Engineers & Developers (Frontend, Backend, Full-Stack)", "DevOps & Cloud Engineers (AWS, Azure, GCP)", "Data Engineers & Data Scientists", "Product Managers & Product Owners", "UX/UI Designers & Researchers", "Solutions Architects & Technical Architects", "QA Engineers & Test Automation Specialists", "IT Project Managers & Scrum Masters", "CTO, VP Engineering & IT Directors", "Cybersecurity Analysts & Information Security Officers"],
    why: "We combine genuine technical understanding with recruitment expertise. Our technology recruiters can discuss your tech stack, your architecture decisions, and your team culture — then use that insight to find candidates who are not just technically capable but genuinely aligned with your engineering philosophy.",
  },
  "accounting-finance": {
    title: "Accounting & Finance",
    subtitle: "Qualified accountants, finance managers, controllers, and senior finance leadership.",
    intro: "Banks Morgan recruits finance professionals for businesses of every size — from high-growth SMEs building their first finance function to multinational corporations strengthening their commercial finance capability. We understand the difference between a strong technical accountant and a commercially minded finance leader, and we recruit for both.",
    roles: ["Qualified Accountants (ACA, ACCA, CIMA)", "Financial Controllers & Finance Managers", "Management Accountants & Cost Analysts", "Finance Directors & CFOs", "Financial Analysts & Business Partners", "Accounts Payable & Receivable Managers", "Payroll Managers & Specialists", "Tax Advisors & Corporation Tax Specialists", "Audit Managers & Internal Audit Leads", "Treasury & Risk Management Professionals"],
    why: "Finance hiring requires precision. A misaligned hire at finance manager or FD level can cost a business significantly — not just in recruitment fees, but in strategic direction. We take the time to understand your financial environment, your reporting lines, and the commercial challenges your finance team faces before presenting candidates.",
  },
  "education-childcare": {
    title: "Education & Childcare",
    subtitle: "Education professionals, nursery management, teaching, and childcare leadership.",
    intro: "Banks Morgan works with nursery groups, multi-academy trusts, independent schools, and education organisations to recruit the leaders and professionals who shape learning environments. From nursery managers and early years practitioners to headteachers and education directors, we understand the standards, safeguarding requirements, and leadership qualities that matter in education.",
    roles: ["Nursery Managers & Deputy Managers", "Early Years Practitioners (Level 2/3)", "Room Leaders & Senior Nursery Nurses", "SENCOs & Inclusion Specialists", "Headteachers & Deputy Headteachers", "Teaching Assistants & Learning Support", "Education Operations Managers", "Regional Managers (Nursery/Education Groups)", "Childcare Quality & Compliance Officers", "Education Directors & CEO-level Leadership"],
    why: "Education and childcare recruitment is not simply about qualifications. The best nursery manager or headteacher brings leadership, emotional intelligence, regulatory understanding, and the ability to build a culture that supports both children and staff. We recruit with those qualities in mind.",
  },
  "commercial-sales": {
    title: "Commercial Sales & Business Development",
    subtitle: "Sales, business development, account management, and commercial leadership.",
    intro: "Every business depends on revenue. Banks Morgan helps organisations recruit the sales professionals, business developers, and commercial leaders who drive growth. From SDRs and BDMs to Sales Directors and Chief Revenue Officers, we understand the competencies, motivations, and cultural factors that separate strong commercial performers from average ones.",
    roles: ["Sales Development Representatives (SDRs/BDRs)", "Business Development Managers", "Account Managers & Key Account Directors", "Account Executives & Sales Executives", "Sales Team Leaders & Sales Managers", "Regional Sales Directors", "Head of Sales & VP Sales", "Chief Revenue Officers & Commercial Directors", "Customer Success Managers & Directors", "Channel Partners & Partnerships Managers"],
    why: "Commercial hiring carries high stakes. A strong sales hire delivers multiples of their salary in revenue. A poor one costs you pipeline, client relationships, and momentum. We assess commercial candidates on track record, process discipline, and cultural alignment — not just charisma.",
  },
};

export function SectorDetailPage() {
  const { slug } = useParams();
  const sector = SECTORS.find(s => s.slug === slug);
  const detail = SECTOR_DETAIL[slug];

  if (!sector || !detail) {
    return (
      <div className="bg-white min-h-screen pt-16 pb-20" data-testid="sector-detail-page">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-gray-400 text-lg py-24">Sector not found.</p>
          <Link to="/sectors" className="text-[11px] font-bold text-[#D4AF37] uppercase tracking-wider">&larr; All Sectors</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="sector-detail-page">
      {/* Hero */}
      <section className="relative h-[300px] md:h-[420px]">
        <img src={SECTOR_IMG[slug]} alt={detail.title} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-[#0F172A]/40 to-[#0F172A]/20" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            <FadeIn>
              <Link to="/sectors" className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider mb-4 inline-block">&larr; All Sectors</Link>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">{detail.title}</h1>
              <p className="text-base text-white/60 mt-3 max-w-2xl">{detail.subtitle}</p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-[15px] text-gray-500 leading-[1.85]">{detail.intro}</p>
          </FadeIn>
        </div>
      </section>

      {/* Key Roles */}
      <section className="bg-[#F8F9FA] py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Key Roles We Recruit</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F172A] mb-10">Typical positions within {detail.title}</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {detail.roles.map((role, i) => (
              <FadeIn key={i} delay={i * 0.03}>
                <div className="flex items-start gap-4 group">
                  <div className="w-8 h-px bg-[#D4AF37] mt-3 flex-shrink-0 group-hover:w-12 transition-all duration-500" />
                  <span className="text-sm text-gray-600">{role}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Why Banks Morgan */}
      <section className="bg-[#0F172A] py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Why Banks Morgan</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-8">Our approach to {detail.title.toLowerCase()} recruitment</h2>
            <p className="text-[15px] text-white/60 leading-[1.85]">{detail.why}</p>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-2xl font-extrabold text-[#0F172A] mb-4">Ready to discuss your {detail.title.toLowerCase()} recruitment?</h2>
            <p className="text-sm text-gray-400 mb-8">Whether you're hiring or looking for your next role in {detail.title.toLowerCase()}, speak to a specialist.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/employers" className="px-8 py-4 text-[12px] font-bold uppercase tracking-[0.15em] bg-[#0F172A] text-white rounded-lg hover:bg-[#1E293B] transition-all">Hire {detail.title.split(" ")[0]} Talent</Link>
              <Link to={`/jobs?sector=${encodeURIComponent(sector.name)}`} className="px-8 py-4 text-[12px] font-bold uppercase tracking-[0.15em] text-[#0F172A] border-2 border-[#0F172A] rounded-lg hover:bg-gray-50 transition-all">View {detail.title.split(" ")[0]} Jobs</Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ═══ CANDIDATES ═══ */
export function CandidatesPage() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", current_title: "", location: "", sector: "", salary_expectation: "", message: "" });
  const [cvFile, setCvFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.email) { toast.error("Please fill in required fields"); return; }
    setSubmitting(true);
    try {
      const res = await api("/public/candidate-register", { method: "POST", body: JSON.stringify(form) });
      if (cvFile && res.candidate_id) { const fd = new FormData(); fd.append("file", cvFile); fd.append("candidate_id", res.candidate_id); await upload("/public/upload-cv", fd); }
      setSubmitted(true); toast.success("Registration successful!");
    } catch { toast.error("Submission failed"); }
    setSubmitting(false);
  };
  return (
    <div className="bg-white min-h-screen pt-8 pb-20" data-testid="candidates-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <FadeIn className="lg:col-span-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-4">For Candidates</div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A] mb-5">Your career, our expertise.</h1>
            <p className="text-sm text-gray-400 leading-relaxed mb-10">Register with Banks Morgan to access exclusive opportunities and specialist career guidance.</p>
            <img src={IMG.candidate} alt="Professional candidate" className="rounded-2xl shadow-md object-cover w-full h-[360px] hidden lg:block" loading="lazy" />
          </FadeIn>
          <FadeIn className="lg:col-span-7" delay={0.1}>
            {submitted ? <SuccessCard title="Registration Complete" msg="Thank you for registering with Banks Morgan. A specialist recruiter will be in touch." /> : (
              <div className="bg-[#F8F9FA] rounded-2xl p-8 md:p-10 space-y-5">
                <div className="grid grid-cols-2 gap-4"><F label="First Name" name="first_name" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} required /><F label="Last Name" name="last_name" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} required /></div>
                <div className="grid grid-cols-2 gap-4"><F label="Email" name="email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /><F label="Phone" name="phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
                <div className="grid grid-cols-2 gap-4"><F label="Current Job Title" name="current_title" value={form.current_title} onChange={e => setForm(f => ({ ...f, current_title: e.target.value }))} /><F label="Location" name="location" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.1em] block mb-2">Sector</label><select value={form.sector} onChange={e => setForm(f => ({ ...f, sector: e.target.value }))} className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3.5 text-sm text-[#0F172A] focus:border-[#D4AF37] outline-none appearance-none"><option value="">Select...</option>{SECTORS.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}</select></div>
                  <F label="Salary Expectation" name="salary_expectation" value={form.salary_expectation} onChange={e => setForm(f => ({ ...f, salary_expectation: e.target.value }))} placeholder="e.g. £40,000" />
                </div>
                <div><label className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.1em] block mb-2">Upload CV<span className="text-gray-300 font-normal ml-1">(PDF, DOC, DOCX)</span></label><input type="file" accept=".pdf,.doc,.docx,.txt,.rtf" onChange={e => setCvFile(e.target.files?.[0] || null)} className="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-[11px] file:font-bold file:uppercase file:tracking-wider file:bg-white file:text-[#0F172A] hover:file:bg-gray-100 file:cursor-pointer" data-testid="cv-upload-register" /></div>
                <F label="Additional Information" name="message" type="textarea" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Career goals, availability..." />
                <GoldBtn onClick={submit} disabled={submitting} className="w-full" data-testid="register-btn">{submitting ? "Registering..." : "Register with Banks Morgan"}</GoldBtn>
              </div>
            )}
          </FadeIn>
        </div>
      </div>
    </div>
  );
}

/* ═══ EMPLOYERS ═══ */
export function EmployersPage() {
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", sector: "", job_title: "", location: "", salary: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => { if (!form.company_name || !form.email) { toast.error("Company and email required"); return; } setSubmitting(true); try { if (form.job_title) { await api("/public/vacancy-submission", { method: "POST", body: JSON.stringify(form) }); } else { await api("/public/employer-enquiry", { method: "POST", body: JSON.stringify(form) }); } setSubmitted(true); toast.success("Enquiry submitted!"); } catch { toast.error("Submission failed"); } setSubmitting(false); };
  return (
    <div className="bg-white min-h-screen pt-8 pb-20" data-testid="employers-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <FadeIn className="lg:col-span-5"><div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-4">For Employers</div><h1 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A] mb-5">Hire exceptional talent.</h1><p className="text-sm text-gray-400 leading-relaxed mb-10">Whether you have an immediate vacancy or want to discuss recruitment strategy, we're ready to help.</p><img src={IMG.employer} alt="Business partnership" className="rounded-2xl shadow-md object-cover w-full h-[360px] hidden lg:block" loading="lazy" /></FadeIn>
          <FadeIn className="lg:col-span-7" delay={0.1}>
            {submitted ? <SuccessCard title="Enquiry Received" msg="A specialist recruitment consultant will contact you shortly." /> : (
              <div className="bg-[#F8F9FA] rounded-2xl p-8 md:p-10 space-y-5">
                <div className="grid grid-cols-2 gap-4"><F label="Company Name" name="company_name" value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} required /><F label="Your Name" name="contact_name" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} /></div>
                <div className="grid grid-cols-2 gap-4"><F label="Email" name="email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /><F label="Phone" name="phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
                <div className="grid grid-cols-2 gap-4"><div><label className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.1em] block mb-2">Sector</label><select value={form.sector} onChange={e => setForm(f => ({ ...f, sector: e.target.value }))} className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3.5 text-sm text-[#0F172A] focus:border-[#D4AF37] outline-none appearance-none"><option value="">Select...</option>{SECTORS.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}</select></div><F label="Job Title (if hiring)" name="job_title" value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))} placeholder="Leave blank for general" /></div>
                <F label="Message" name="message" type="textarea" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Tell us about your recruitment needs..." />
                <GoldBtn onClick={submit} disabled={submitting} className="w-full" data-testid="employer-submit">{submitting ? "Submitting..." : form.job_title ? "Submit Vacancy" : "Submit Enquiry"}</GoldBtn>
              </div>
            )}
          </FadeIn>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/* PARTNERSHIPS — Full editorial copy          */
/* ═══════════════════════════════════════════ */
export function PartnershipsPage() {
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", hiring_frequency: "", expected_vacancies: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => { if (!form.company_name || !form.email) { toast.error("Company and email required"); return; } setSubmitting(true); try { await api("/public/partnership-enquiry", { method: "POST", body: JSON.stringify(form) }); setSubmitted(true); toast.success("Enquiry submitted!"); } catch { toast.error("Submission failed"); } setSubmitting(false); };
  return (
    <div className="min-h-screen" data-testid="partnerships-page">
      {/* Hero section */}
      <section className="relative bg-[#0F172A] overflow-hidden">
        <img src={IMG.partnership} alt="Partnership" className="absolute inset-0 w-full h-full object-cover opacity-20" loading="lazy" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
          <FadeIn>
            <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Recruitment Partnerships</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-6">More than recruitment.<br />A long-term talent partnership.</h1>
          </FadeIn>
        </div>
      </section>

      {/* Section 1 — Intro */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="space-y-6 text-[15px] text-gray-500 leading-[1.85]">
              <p>At Banks Morgan, we believe recruitment should be about more than filling a vacancy.</p>
              <p>A great hire can strengthen a team, change the direction of a business and create value long after the appointment has been made. That is why we focus on building long-term relationships with the businesses we work with rather than treating recruitment as a series of individual transactions.</p>
              <p>Our approach is built around partnership.</p>
              <p>We take the time to understand your organisation, your culture, your ambitions and the people you need to achieve them. We then use our specialist recruitment expertise, technology and talent network to help you attract and secure the right people.</p>
              <p>Whether you recruit occasionally, are experiencing rapid growth or have an ongoing requirement for high-quality talent, we can build a recruitment approach around your business.</p>
              <p className="text-[#0F172A] font-semibold">You don't simply gain another recruitment supplier.</p>
              <p>You gain a recruitment partner that understands your business, works alongside your team and remains focused on the quality of every appointment.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Section 2 — The Partnership (dark) */}
      <section className="bg-[#0F172A] py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <FadeIn className="lg:col-span-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-4">The Banks Morgan Partnership</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight mb-6">A long-term relationship allows us to become increasingly valuable to your organisation.</h2>
            </FadeIn>
            <FadeIn className="lg:col-span-7" delay={0.1}>
              <p className="text-[15px] text-white/60 leading-[1.85] mb-8">Over time, we develop a deeper understanding of:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {["your business", "your culture", "your teams", "your hiring priorities", "the skills you need", "the markets in which you operate", "the type of people who succeed within your organisation"].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <span className="text-sm text-white/70">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[15px] text-white/60 leading-[1.85] mt-8">This means every future search can become more informed, more targeted and more aligned with your business.</p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Section 3 — Built around your business */}
      <section className="bg-[#F8F9FA] py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <FadeIn className="lg:col-span-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Built Around Your Business</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F172A] leading-tight mb-6">Every organisation recruits differently.</h2>
              <p className="text-[15px] text-gray-500 leading-[1.85]">Our partnership approach can support:</p>
            </FadeIn>
            <FadeIn className="lg:col-span-6" delay={0.1}>
              <div className="space-y-4 mt-2">
                {[
                  "occasional recruitment requirements",
                  "recurring vacancies",
                  "multiple hires",
                  "difficult-to-fill positions",
                  "growth programmes",
                  "specialist recruitment",
                  "ongoing talent requirements",
                  "additional recruitment capacity alongside an internal HR team",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 group">
                    <div className="w-8 h-px bg-[#D4AF37] mt-3 flex-shrink-0 group-hover:w-12 transition-all duration-500" />
                    <span className="text-sm text-gray-500">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[15px] text-gray-500 leading-[1.85] mt-8">We can adapt the relationship around the level of recruitment support your business actually needs.</p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Section 4 — Technology + Human Expertise */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Technology + Human Expertise</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F172A] leading-tight mb-8">Technology helps us work faster and identify opportunities that traditional recruitment approaches can miss.</h2>
            <p className="text-[15px] text-gray-500 leading-[1.85] mb-6">But technology does not replace human judgement.</p>
            <p className="text-[15px] text-gray-500 leading-[1.85] mb-12">Our recruitment specialists combine technology, market intelligence, candidate research and genuine relationship-building to deliver a more informed recruitment process.</p>
            <div className="bg-[#F8F9FA] rounded-2xl p-10 md:p-14">
              <p className="text-sm text-gray-400 uppercase tracking-widest font-bold mb-6">The objective is simple</p>
              <div className="space-y-2">
                {["Find the right person.", "For the right role.", "At the right time.", "For the right business."].map((line, i) => (
                  <p key={i} className="text-lg md:text-xl font-extrabold text-[#0F172A]">{line}</p>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Section 5 — Not a transaction */}
      <section className="bg-[#0F172A] py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-4">A Partnership, Not a Transaction</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight mb-8">We are interested in the relationship beyond the immediate vacancy.</h2>
            <p className="text-[15px] text-white/60 leading-[1.85] mb-6">When we understand where your business is going, we can help you prepare for the people you will need next — not simply react to the vacancy you have today.</p>
            <p className="text-[15px] text-white/80 leading-[1.85] font-medium">That is the difference between using a recruitment agency and having a recruitment partner.</p>
          </FadeIn>
        </div>
      </section>

      {/* CTA + Form */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Let's Talk</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F172A] mb-4">Let's talk about your talent requirements</h2>
              <p className="text-[15px] text-gray-400 max-w-xl mx-auto">Whether you have an immediate vacancy or are planning for future growth, speak to Banks Morgan about building a recruitment partnership around your business.</p>
            </div>
          </FadeIn>
          {submitted ? <SuccessCard title="Enquiry Received" msg="Our partnerships team will be in touch to discuss your requirements." /> : (
            <FadeIn delay={0.1}><div className="bg-[#F8F9FA] rounded-2xl p-8 md:p-10 space-y-5 max-w-2xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5"><F label="Company" name="company_name" value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} required /><F label="Your Name" name="contact_name" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5"><F label="Email" name="email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /><F label="Phone" name="phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5"><F label="How often do you recruit?" name="hiring_frequency" value={form.hiring_frequency} onChange={e => setForm(f => ({ ...f, hiring_frequency: e.target.value }))} /><F label="Expected vacancies this year" name="expected_vacancies" value={form.expected_vacancies} onChange={e => setForm(f => ({ ...f, expected_vacancies: e.target.value }))} /></div>
              <F label="Message" name="message" type="textarea" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
              <GoldBtn onClick={submit} disabled={submitting} className="w-full" data-testid="partnership-submit">{submitting ? "Submitting..." : "Discuss a Partnership"}</GoldBtn>
            </div></FadeIn>
          )}
        </div>
      </section>
    </div>
  );
}

/* ═══ ABOUT ═══ */
export function AboutPage() {
  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero image — full width editorial */}
      <section className="relative h-[300px] md:h-[480px]">
        <img src={IMG.about} alt="Banks Morgan team" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/30 to-transparent" />
      </section>

      <section className="bg-white pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
          <FadeIn>
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-14 mb-16">
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-3">About</div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A] mb-5">Banks Morgan Recruitment.</h1>
              <p className="text-lg text-gray-400 leading-relaxed max-w-2xl">A specialist recruitment consultancy connecting exceptional professionals with ambitious businesses across five core sectors.</p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { t: "Our Approach", d: "We combine deep sector knowledge with modern recruitment technology to deliver a service that is faster, more precise, and more commercially intelligent than traditional recruitment." },
              { t: "For Candidates", d: "We represent candidates with the same seriousness we bring to every client brief. Your career goals, your confidentiality, and your reputation are central to everything we do." },
              { t: "For Employers", d: "We understand that recruitment is an investment. Our commercial models are designed to align with your business reality, not a generic agency rate card." },
              { t: "Technology", d: "From candidate research and market intelligence to vacancy matching and talent analytics, technology supports every stage — while keeping people at the centre." },
            ].map((s, i) => (
              <FadeIn key={i} delay={i * 0.06}>
                <div className="border-l-2 border-[#D4AF37] pl-8 py-2">
                  <h2 className="text-lg font-bold text-[#0F172A] mb-3">{s.t}</h2>
                  <p className="text-sm text-gray-400 leading-relaxed">{s.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ═══ CONTACT ═══ */
export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", enquiry_type: "general", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => { if (!form.name || !form.email) { toast.error("Name and email required"); return; } setSubmitting(true); try { await api("/public/contact", { method: "POST", body: JSON.stringify(form) }); setSubmitted(true); toast.success("Message sent!"); } catch { toast.error("Submission failed"); } setSubmitting(false); };
  return (
    <div className="bg-white min-h-screen pt-8 pb-20" data-testid="contact-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn><div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37] mb-3">Contact</div><h1 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A] mb-10">Get in touch.</h1></FadeIn>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <FadeIn className="lg:col-span-2">
            {submitted ? <SuccessCard title="Message Sent" msg="We'll be in touch shortly." /> : (
              <div className="bg-[#F8F9FA] rounded-2xl p-8 md:p-10 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5"><F label="Name" name="name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /><F label="Email" name="email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /></div>
                <F label="Phone" name="phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                <div><label className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.1em] block mb-2">Enquiry Type</label><select value={form.enquiry_type} onChange={e => setForm(f => ({ ...f, enquiry_type: e.target.value }))} className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3.5 text-sm text-[#0F172A] focus:border-[#D4AF37] outline-none appearance-none"><option value="general">General Enquiry</option><option value="employer">Employer / Hiring</option><option value="candidate">Candidate / Job Seeker</option><option value="partnership">Recruitment Partnership</option></select></div>
                <F label="Message" name="message" type="textarea" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} required />
                <GoldBtn onClick={submit} disabled={submitting} className="w-full" data-testid="contact-submit">{submitting ? "Sending..." : "Send Message"}</GoldBtn>
              </div>
            )}
          </FadeIn>
          <FadeIn className="space-y-6" delay={0.1}>
            <div className="bg-[#F8F9FA] rounded-2xl p-8"><h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-4">Call Us</h3><a href="tel:02045850050" className="text-2xl font-extrabold text-[#D4AF37] hover:text-[#B8941F] transition-colors" data-testid="contact-phone">020 4585 0050</a></div>
            <div className="bg-[#F8F9FA] rounded-2xl p-8"><h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-4">Banks Morgan Recruitment</h3><p className="text-sm text-gray-400 leading-relaxed">Specialist recruitment across Healthcare, Technology, Finance, Education, and Commercial sectors.</p></div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
}

/* ═══ JOB ALERT UNSUBSCRIBE ═══ */
export function JobAlertUnsubscribePage() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading");
  useEffect(() => {
    if (!token) { setStatus("error"); return; }
    api(`/public/job-alerts/unsubscribe/${token}`).then(r => {
      setStatus(r.unsubscribed ? "done" : "already");
    }).catch(() => setStatus("error"));
  }, [token]);
  return (
    <div className="bg-white min-h-screen pt-16 pb-20" data-testid="unsubscribe-page">
      <div className="max-w-lg mx-auto px-4 text-center">
        {status === "loading" && <p className="text-gray-400 py-24">Processing...</p>}
        {status === "done" && <SuccessCard title="Unsubscribed" msg="You have been unsubscribed from this job alert. You will no longer receive emails for this alert." />}
        {status === "already" && <SuccessCard title="Already Unsubscribed" msg="This alert was already inactive." />}
        {status === "error" && <div className="py-24"><p className="text-gray-400 text-lg">Could not process your request.</p></div>}
        <Link to="/jobs" className="inline-block mt-8 text-[11px] font-bold text-[#D4AF37] uppercase tracking-wider hover:text-[#B8941F]">Browse Jobs &rarr;</Link>
      </div>
    </div>
  );
}

/* ═══ LAYOUT ═══ */
export default function PublicSiteLayout({ children }) {
  return (
    <div className="bg-white min-h-screen" style={{ fontFamily: "'Cabinet Grotesk', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <SiteHeader />
      <main className="pt-[90px] md:pt-[110px]">{children}</main>
      <SiteFooter />
    </div>
  );
}
